import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportService } from '@/api/services/report.service';
import { apiService } from '@/api/services/api.service';
import type { MappedPublicReport } from '@/api/mappers/public-report.mapper';

const LINK_ID_STORAGE_KEY = 'reportAccessLinkId';
const REPORT_ID_STORAGE_KEY = 'reportAccessReportId';

type ApiError = { status?: number; error?: string; message?: string };

interface UsePublicReportParams {
  id?: string;
  token?: string;
}

interface UsePublicReportResult {
  reportData: MappedPublicReport | null;
  loading: boolean;
  error: string | null;
}

const buildReportAccessPath = (linkId?: string | null): string =>
  linkId ? `/report-access/${linkId}` : '/report-access/';

export const usePublicReport = ({ id, token }: UsePublicReportParams): UsePublicReportResult => {
  const navigate = useNavigate();
  const [reportData, setReportData] = useState<MappedPublicReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReport = async () => {
      if (!id) {
        setError('Brak ID raportu w URL');
        setLoading(false);
        return;
      }

      // URL pokazuje inny raport niż ten powiązany z aktywną sesją. Backend rozwiązuje
      // raport z tokenu JWT (a nie z `id` w URL), więc fetch zwróciłby stary raport.
      // Blokujemy fetch i pokazujemy komunikat, ale nie czyścimy sesji — user wciąż
      // może wrócić do oryginalnego raportu bez ponownego logowania.
      const savedReportId = sessionStorage.getItem(REPORT_ID_STORAGE_KEY);
      if (!token && savedReportId && savedReportId !== id) {
        setReportData(null);
        setError(
          'Ten raport wymaga osobnego zalogowania. Otwórz link z wiadomości email dla tego raportu.',
        );
        setLoading(false);
        return;
      }

      if (!token && !apiService.getAuthToken()) {
        const savedLinkId = sessionStorage.getItem(LINK_ID_STORAGE_KEY);
        navigate(buildReportAccessPath(savedLinkId), { replace: true });
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await reportService.getPublicReport(token);

        if (response.data) {
          setReportData(response.data);
        } else {
          setError('Brak danych w odpowiedzi z serwera');
        }
      } catch (err) {
        const apiErr = err as ApiError;
        if (apiErr?.status === 401) {
          const savedLinkId = sessionStorage.getItem(LINK_ID_STORAGE_KEY);
          if (savedLinkId) {
            navigate(`/report-access/${savedLinkId}`, { replace: true });
            return;
          }
          setError('Sesja wygasła. Otwórz link z wiadomości email ponownie.');
        } else {
          setError(apiErr?.message || 'Nie udało się pobrać danych raportu');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [id, token, navigate]);

  return { reportData, loading, error };
};
